import ArrowLeftIcon from "/public/images/icons/arrowLeft.svg?react";
import SearchIcon from "/public/images/icons/search.svg?react";
import FilterIcon from "/public/images/icons/filter.svg?react";
import CartIcon from "/public/images/icons/cartIcon.svg?react";
import AddIcon from "/public/images/icons/add.svg?react";
import React, { useEffect, useState } from "react"; 
import { useNavigate } from "react-router-dom";
import { useHeaderContext } from "../../context/headerContext";
import { useCartContext } from "../../context/cartContext";
import { useUserContext } from "../../context/userContext";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { useThemeContext } from "../../context/themeContext";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

const MobileHeader = () => {
  const {
    title,
    searchFunc,
    searchTriggered,
    emergency,
    editFunc,
    backButton,
    backButtonFunc,
    filterFunc,
    cartButtonFunc,
    addFunc
  } = useHeaderContext();

  const { addCart, cart } = useCartContext();
  const { user, logout, updateTokens } = useUserContext();
  const { theme } = useThemeContext();

  const [totalQuantity, setTotalQuantity] = useState(0);
  let navigate = useNavigate();

  const handleBackClick = () => {
    if (backButtonFunc) {
      backButtonFunc();
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (theme === "participant") {
      fetchCart();
    }
  }, []);

  const fetchCart = async () => {
    await Sentry.startSpan(
      {
        name: "Fetch Cart",
        op: "http.request",
      },
      async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Cart/Cart?userAccountId=${user.id}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );

          // Attach HTTP status to the span
          span.setAttribute("http.status_code", response.status);

          if (response.ok) {
            const text = await response.text();
            try {
              const data = JSON.parse(text);
              addCart(data);
            } catch (e) {
              if (text === 'Cart is empty') {
                addCart([]);
              } else {
                throw new Error("Failed to fetch cart");
              }
            }
          } else {
            span.setStatus("error"); // Mark as error
            throw new Error("Failed to fetch cart");
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error); // Log error to Sentry
          LogErrors(user, logout, updateTokens, error);
        } finally {
          span.end(); // Ensure span ends properly
        }
      }
    );
  };

  useEffect(() => {
    setTotalQuantity(cart?.cartItems?.reduce((sum, item) => sum + item.Quantity, 0));
  }, [cart.cartItems]);

  return (
    <>
      {title && (
        <header
          className={`flex justify-between items-center p-3 text-[12px] border-b-[1px] min-h-[49px] border-[#E3E3E3] ${
            emergency ? "bg-sportsRed text-white" : ""
          }`}
        >
          {backButton ? (
            <div onClick={handleBackClick} className="back-button" data-testid="back-button">
              <ArrowLeftIcon
                className={`w-[20px] cursor-pointer ${
                  emergency ? "fill-white stroke-white" : "stroke-black"
                }`}
              />
            </div>
          ) : (
            <div className="w-[20px]"></div>
          )}
          <div className="title uppercase font-semibold tracking-widest">
            {title}
          </div>
          <div className="search-icon min-w-[25px] text-center">
            {searchFunc && (
              <SearchIcon
                data-testid="search-button"
                onClick={searchFunc}
                className={`
                  w-[20px] cursor-pointer stroke-black
                  ${emergency && "!stroke-white"}
                  ${searchTriggered && "!stroke-sportsYellow"}
                `}
              />
            )}
            {editFunc && (
              <div
                data-testid="edit-button"
                onClick={editFunc}
                className="underline uppercase font-semibold tracking-widest cursor-pointer"
              >
                Edit
              </div>
            )}
            {filterFunc && (
              <div
                data-testid="filter-button"
                onClick={filterFunc}
                className="underline uppercase font-semibold tracking-widest cursor-pointer"
              >
                <FilterIcon
                  className={`w-[20px] cursor-pointer ${
                    emergency ? "stroke-white" : "stroke-black"
                  }`}
                />
              </div>
            )}
            {addFunc && (
              <div
                data-testid="add-button"
                onClick={addFunc}
                className="underline uppercase font-semibold tracking-widest cursor-pointer"
              >
                <AddIcon
                  className={`w-[20px] cursor-pointer ${
                    emergency ? "stroke-white" : "stroke-black"
                  }`}
                />
              </div>
            )}
            {cartButtonFunc && user?.subscription?.Name !== "Lite" && (
              <div
                data-testid="cart-button"
                onClick={cartButtonFunc}
                className={`uppercase font-semibold tracking-widest cursor-pointer relative`}
              >
                {totalQuantity > 0 && (
                  <div className="w-[18px] h-[18px] bg-sportsYellow rounded-full text-center absolute top-[-6px] right-[-4px]">
                    <span className="text-sportsBlack text-[11px] font-medium leading-[11px] tracking-normal">{totalQuantity}</span>
                  </div>
                )}
                <CartIcon
                  className={`w-[20px] cursor-pointer stroke-black`}
                />
              </div>
            )}
          </div>
        </header>
      )}
    </>
  );
};

export default MobileHeader;